/**
 * Settings > Preview & skins > Skins (W2-13 settings-keymap; DESIGN.md §4.20, ARCHITECTURE.md §12).
 *
 * The installed skins (respacks) with where each comes from — drawn by Phiedit (procedural), the Phigros skin read
 * from local files (development builds only), or an imported Phira resource pack — and which one is in use. The
 * default is a choice between "Automatic" (settings.preview.defaultSkin = null: the resolution order local Phigros
 * skin, then the drawn skin) and one installed skin. "Import skin (.zip)" sends the archive to the io worker, which
 * unzips and validates it like Phira does; a resource pack is then shown in the install dialog (W2-09's
 * `DIALOG.skinInstall`) and becomes the default once installed. Imported packs can be removed.
 */

import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import type { MessageRef } from '../../core/util/types';
import type { SkinSummary } from '../../render/types';
import { Button, Icon, IconButton } from '../../ui';
import { readBytes } from './files';
import { useSettingsValue, type SettingsText } from './hooks';
import { m } from './settings.messages';
import s from './Settings.module.css';

const SOURCE_LABEL: Readonly<Record<SkinSummary['source'], MessageKey>> = {
  procedural: m.skinSourceProcedural,
  localPhigros: m.skinSourceLocal,
  respack: m.skinSourceRespack,
};

const SOURCE_ICON: Readonly<Record<SkinSummary['source'], string>> = {
  procedural: 'Shapes',
  localPhigros: 'FolderOpen',
  respack: 'Package',
};

/** The skin "Automatic" resolves to among the installed ones (local Phigros skin, else the drawn skin). */
export function automaticSkin(list: readonly SkinSummary[]): SkinSummary | null {
  return (
    list.find((x) => x.source === 'localPhigros') ?? list.find((x) => x.source === 'procedural') ?? list[0] ?? null
  );
}

export interface SkinsSectionProps {
  readonly services: AppServices;
  readonly text: SettingsText;
}

export function SkinsSection(props: SkinsSectionProps) {
  const { services, text } = props;
  const { t, tr } = text;
  const settings = useSettingsValue(services);
  const groupName = useId();
  const [list, setList] = useState<readonly SkinSummary[]>(() => services.skins.list());
  const [activeId, setActiveId] = useState(() => services.skins.active.id);
  // Why skins were skipped while resolving (SkinStore.problems: local skin invalid, a stored pack damaged...).
  const [problems, setProblems] = useState<readonly MessageRef[]>(() => services.skins.problems?.() ?? []);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const defaultSkin = settings.preview.defaultSkin;

  useEffect(() => {
    const store = services.skins;
    let alive = true;
    const refresh = (): void => {
      if (!alive) return;
      setList(store.list());
      setActiveId(store.active.id);
      setProblems(store.problems?.() ?? []);
    };
    void store.ready.then(refresh);
    const off = store.subscribe(refresh);
    return () => {
      alive = false;
      off();
    };
  }, [services.skins]);

  const choose = async (id: string | null): Promise<void> => {
    services.settings.set('preview', 'defaultSkin', id);
    const target = id ?? automaticSkin(services.skins.list())?.id ?? null;
    if (target !== null && target !== services.skins.active.id) {
      try {
        await services.skins.setActive(target);
      } catch {
        services.ui.toast({ key: m.skinActivateFailed }, { kind: 'error' });
      }
    }
    setList(services.skins.list());
    setActiveId(services.skins.active.id);
  };

  const remove = async (skin: SkinSummary): Promise<void> => {
    const ok = await services.ui.confirm({
      title: { key: m.skinRemoveTitle, params: { name: skin.name } },
      body: { key: m.skinRemoveBody },
      confirm: { key: m.skinRemoveConfirm },
      danger: true,
    });
    if (!ok) return;
    if (services.settings.value.preview.defaultSkin === skin.id) await choose(null);
    await services.skins.remove(skin.id);
    setList(services.skins.list());
    setActiveId(services.skins.active.id);
    services.ui.toast({ key: m.skinRemoved, params: { name: skin.name } }, { kind: 'success' });
  };

  /** Installs a validated pack without the install dialog (when no package registers it). */
  const install = async (pack: Parameters<AppServices['skins']['install']>[0]): Promise<string | null> => {
    const res = await services.skins.install(pack);
    if (res.ok) return res.id;
    services.ui.toast(res.errors[0] ?? { key: m.skinInvalid }, { kind: 'error' });
    return null;
  };

  const importZip = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file === undefined) return;
    setBusy(true);
    try {
      const bytes = await readBytes(file);
      const result = await services.io.call('importPackage', { input: { kind: 'zip', name: file.name, bytes } });
      if (result.kind === 'error') {
        services.ui.toast(result.error, { kind: 'error' });
        return;
      }
      if (result.kind !== 'respack') {
        services.ui.toast({ key: m.skinNotARespack, params: { name: file.name } }, { kind: 'warning' });
        return;
      }
      let installed: string | null;
      if (services.registry.dialog(DIALOG.skinInstall) !== undefined) {
        // The install dialog shows Phira's validation (errors block, warnings explained) and installs.
        const res = await services.ui.openDialog(DIALOG.skinInstall, {
          pack: result.pack,
          validation: result.validation,
        });
        installed = res?.installedId ?? null;
      } else if (result.pack === null || result.validation.errors.length > 0) {
        services.ui.toast(result.validation.errors[0] ?? { key: m.skinInvalid }, { kind: 'error' });
        installed = null;
      } else {
        installed = await install(result.pack);
      }
      if (installed === null) return;
      await choose(installed);
      const name = services.skins.list().find((x) => x.id === installed)?.name ?? file.name;
      services.ui.toast({ key: m.skinInstalled, params: { name } }, { kind: 'success' });
    } catch (err) {
      services.ui.toast(
        { key: m.skinImportFailed, params: { detail: err instanceof Error ? err.message : String(err) } },
        { kind: 'error' },
      );
    } finally {
      setBusy(false);
    }
  };

  const auto = automaticSkin(list);

  return (
    <div className={s.skins}>
      <div className={s.blockHeader}>
        <div>
          <h3 className={s.blockTitle}>{t(m.skinsTitle)}</h3>
          <p className={s.blockDesc}>{t(m.skinsDesc)}</p>
        </div>
        <Button
          icon="Upload"
          disabledReason={busy ? { key: m.skinImporting } : null}
          onClick={() => {
            fileInput.current?.click();
          }}
        >
          {t(m.skinImport)}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".zip,application/zip"
          hidden
          aria-label={t(m.skinImport)}
          onChange={(e) => {
            void importZip(e);
          }}
        />
      </div>
      <div role="radiogroup" aria-label={t(m.skinDefaultLabel)} className={s.skinList}>
        <label className={s.skinItem} data-selected={defaultSkin === null ? 'true' : undefined}>
          <input
            type="radio"
            name={groupName}
            checked={defaultSkin === null}
            onChange={() => {
              void choose(null);
            }}
          />
          <Icon name="Sparkles" size={18} />
          <span className={s.skinText}>
            <span className={s.skinName}>{t(m.skinAutomatic)}</span>
            <span className={s.skinMeta}>
              {auto === null ? t(m.skinAutomaticDesc) : t(m.skinAutomaticNow, { name: auto.name })}
            </span>
          </span>
        </label>
        {list.map((skin) => (
          <div key={skin.id} className={s.skinItem} data-selected={defaultSkin === skin.id ? 'true' : undefined}>
            <label className={s.skinLabel}>
              <input
                type="radio"
                name={groupName}
                checked={defaultSkin === skin.id}
                onChange={() => {
                  void choose(skin.id);
                }}
              />
              <Icon name={SOURCE_ICON[skin.source]} size={18} />
              <span className={s.skinText}>
                <span className={s.skinName}>{skin.name}</span>
                <span className={s.skinMeta}>
                  {t(SOURCE_LABEL[skin.source])}
                  {skin.author !== '' && ` · ${t(m.skinBy, { author: skin.author })}`}
                </span>
              </span>
            </label>
            {activeId === skin.id && (
              <span className={s.tag} data-kind="active">
                <Icon name="Check" size={12} />
                {t(m.skinInUse)}
              </span>
            )}
            {skin.source === 'respack' && (
              <IconButton
                icon="Trash"
                size="sm"
                label={{ key: m.skinRemove, params: { name: skin.name } }}
                onClick={() => {
                  void remove(skin);
                }}
              />
            )}
          </div>
        ))}
      </div>
      {list.length === 0 && <p className={s.blockDesc}>{tr({ key: m.skinNone })}</p>}
      {problems.length > 0 && (
        <div className={s.skinProblems} role="note">
          <Icon name="TriangleAlert" size={14} />
          <div>
            <p className={s.skinProblemsTitle}>{t(m.skinProblems)}</p>
            <ul className={s.skinProblemList}>
              {problems.map((p, i) => (
                <li key={i}>{tr(p)}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
